import React, { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { fetchEditTargets, sendEditToProject, type EditHandoffResult, type EditTarget } from '../../services/editingApi';
import { formatStatusLabel } from '../../utils/projectStatus';

interface SendToEditorDialogProps {
  open: boolean;
  editId: string;
  editVersion: number;
  /** Unsaved changes would be missed: the project gets what's saved. */
  dirty: boolean;
  onClose: () => void;
  onSent: (result: EditHandoffResult) => void;
}

/** Pick one of the creator's open projects of this video; the edit becomes its tasks. */
const SendToEditorDialog: React.FC<SendToEditorDialogProps> = ({ open, editId, editVersion, dirty, onClose, onSent }) => {
  const [targets, setTargets] = useState<EditTarget[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [sending, setSending] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setTargets(null);
    setProblem(null);
    fetchEditTargets(editId)
      .then((list) => { if (!cancelled) setTargets(list); })
      .catch((error) => { if (!cancelled) { setTargets([]); setProblem(error instanceof Error ? error.message : 'Your projects couldn’t be loaded.'); } });
    return () => { cancelled = true; };
  }, [editId, open]);

  const send = async (target: EditTarget) => {
    setSending(target.id);
    setProblem(null);
    try {
      onSent(await sendEditToProject(editId, target.id));
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'It couldn’t be sent.');
    } finally {
      setSending(null);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Send this edit to my editor</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The edit becomes tasks on one of your projects for this video, and the hand-off package gets a brief, the edit as data and a timeline file. Sending again replaces the earlier tasks.
        </Typography>
        {dirty ? <Alert severity="info" sx={{ mb: 1.5 }}>Unsaved changes are saved first in a moment; wait for “Saved” before sending.</Alert> : null}
        {problem ? <Alert severity="error" sx={{ mb: 1.5 }}>{problem}</Alert> : null}
        {targets === null ? <LinearProgress /> : targets.length === 0 ? (
          <Alert severity="info">
            No open project uses this video yet. <RouterLink to="/submit-editing-request/">Create a project</RouterLink> from it first, then send the edit there.
          </Alert>
        ) : (
          <List dense disablePadding>
            {targets.map((target) => (
              <ListItemButton key={target.id} onClick={() => send(target)} disabled={Boolean(sending)} data-testid={`send-target-${target.id}`} sx={{ borderRadius: 1 }}>
                <ListItemText
                  primary={target.projectName}
                  secondary={`${formatStatusLabel(target.status)}${target.editorName ? ` · ${target.editorName}` : ''}${target.attachedEditVersion !== null ? ` · has version ${target.attachedEditVersion}${target.attachedEditVersion === editVersion ? ' (this one)' : ''}` : ''}`}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={Boolean(sending)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendToEditorDialog;
